// Shell layout state remembered in localStorage (CONTRACT "Shell geometry"):
// list open, list width (240 to 320), tape open per destination class,
// inspector open. Pure read and write helpers with a storage seam so tests
// never touch window; a throwing storage (private mode, quota) reads as the
// default and writes report false.
import type { ViewId } from '../router/useView';

export const SHELL_KEYS = {
  list: 'aleph.list',
  listWidth: 'aleph.listw',
  tape: 'aleph.tape',
  inspector: 'aleph.inspector',
  checklist: 'aleph.checklist',
} as const;

export const LIST_WIDTH = { min: 240, max: 320, initial: 256 } as const;

export interface ShellStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export type OpenState = 'open' | 'closed';

export interface ShellPrefs {
  readonly list: OpenState;
  // null until the reader drags the handle. Until then the destination's own
  // default applies (256 everywhere, 320 on Coin Checker); once dragged, the
  // stored width wins on every destination.
  readonly listWidth: number | null;
  readonly tape: Readonly<Record<TapeClass, OpenState>>;
  readonly inspector: OpenState;
  readonly checklist: OpenState;
}

// Live Calls is its own tape (the stream), so its class defaults closed;
// every other destination defaults open (SPEC D12).
export type TapeClass = 'live' | 'other';

export function tapeClassOf(view: ViewId): TapeClass {
  return view === 'live' ? 'live' : 'other';
}

export const DEFAULT_PREFS: ShellPrefs = {
  list: 'open',
  listWidth: null,
  tape: { live: 'closed', other: 'open' },
  inspector: 'closed',
  checklist: 'open',
};

function isOpenState(value: string | null): value is OpenState {
  return value === 'open' || value === 'closed';
}

export function clampListWidth(value: number): number {
  if (!Number.isFinite(value)) return LIST_WIDTH.initial;
  return Math.min(LIST_WIDTH.max, Math.max(LIST_WIDTH.min, Math.round(value)));
}

function readKey(storage: ShellStorage | null, key: string): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

export function readPrefs(storage: ShellStorage | null): ShellPrefs {
  const list = readKey(storage, SHELL_KEYS.list);
  const width = Number(readKey(storage, SHELL_KEYS.listWidth));
  const tapeLive = readKey(storage, `${SHELL_KEYS.tape}.live`);
  const tapeOther = readKey(storage, `${SHELL_KEYS.tape}.other`);
  const inspector = readKey(storage, SHELL_KEYS.inspector);
  const checklist = readKey(storage, SHELL_KEYS.checklist);
  return {
    list: isOpenState(list) ? list : DEFAULT_PREFS.list,
    listWidth: Number.isFinite(width) && width > 0 ? clampListWidth(width) : DEFAULT_PREFS.listWidth,

    tape: {
      live: isOpenState(tapeLive) ? tapeLive : DEFAULT_PREFS.tape.live,
      other: isOpenState(tapeOther) ? tapeOther : DEFAULT_PREFS.tape.other,
    },
    inspector: isOpenState(inspector) ? inspector : DEFAULT_PREFS.inspector,
    checklist: isOpenState(checklist) ? checklist : DEFAULT_PREFS.checklist,
  };
}

export function writeKey(storage: ShellStorage | null, key: string, value: string): boolean {
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function toggled(state: OpenState): OpenState {
  return state === 'open' ? 'closed' : 'open';
}

export function browserStorage(): ShellStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
